// src/pages/QuoteFormPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatRut, formatCurrency, formatPhone } from '../utils/formatters';
import imageCompression from 'browser-image-compression';
import { PhotoIcon, DocumentTextIcon, ArchiveBoxIcon, TrashIcon } from '@heroicons/react/24/solid';

// Servicios
import { quoteService } from '../services/quoteService';
import { clientService } from '../services/clientService';
import { productService } from '../services/productService';

// --- HELPER: Convertir link de Drive a Imagen Directa ---
const getDirectUrl = (url) => {
    if (!url) return '';
    if (!url.includes('drive.google.com')) return url;
    try {
        let id = '';
        const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
        if (fileIdMatch && fileIdMatch[1]) { id = fileIdMatch[1]; } 
        else {
            const idParamMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
            if (idParamMatch && idParamMatch[1]) { id = idParamMatch[1]; }
        }
        if (id) return `https://lh3.googleusercontent.com/d/${id}`;
        return url; 
    } catch (e) { return url; }
};

// --- Componente de Contenido del Formulario ---
const FormContent = ({
    items, handleItemChange, addItemRow, removeItemRow, parameters,
    rut, handleRutChange, handleRutBlur, 
    clientName, clientNameSearch, handleClientNameSearchChange, handleSelectClient, clientNameInputRef, clientSuggestions, showClientSuggestions, clientSuggestionsRef,
    address, setAddress, phone, setPhone, email, setEmail, encargado, setEncargado, paymentMethod, setPaymentMethod,
    entrega, setEntrega, validez, setValidez, details, setDetails,
    photoPreviews, removePhoto, uploadProgress, fileInputRef, handlePhotoChange,
    subtotal, ivaAmount, total,
    handleDescriptionSearchChange, handleProductSelect
}) => (
    <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg print:shadow-none print:border transition-colors">
            <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200 print:text-base transition-colors">Datos del Cliente</h2>
             <div className="grid grid-cols-1 md:grid-cols-12 print:grid-cols-12 gap-x-6 gap-y-4 items-center print:gap-x-4 print:gap-y-2">
                
                {/* Cliente (con búsqueda) */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Cliente</label>
                <div className="md:col-span-5 print:col-span-5 relative">
                    <input 
                        type="text" 
                        value={clientNameSearch} 
                        onChange={handleClientNameSearchChange} 
                        ref={clientNameInputRef} 
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" 
                        required 
                        autoComplete="off"
                    />
                    {showClientSuggestions && clientSuggestions.length > 0 && (
                        <ul ref={clientSuggestionsRef} className="absolute z-20 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg text-gray-900 dark:text-gray-100 transition-colors">
                            {clientSuggestions.map(client => (
                                <li
                                    key={client.id}
                                    onClick={() => handleSelectClient(client)}
                                    className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm transition-colors"
                                >
                                    {client.nombre} ({formatRut(client.rut || client.id)})
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* RUT */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">RUT</label>
                <input type="text" value={rut} maxLength="12" onChange={handleRutChange} onBlur={handleRutBlur} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" required />

                {/* Dirección */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Dirección</label>
                <input type="text" value={address} onChange={e => setAddress(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />

                {/* Fecha */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Fecha</label>
                <input type="date" value={new Date().toISOString().split('T')[0]} readOnly className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-gray-300 print:border-none print:p-0 print:bg-transparent transition-colors" />

                {/* Teléfono */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Teléfono</label>
                <input type="text" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />

                {/* Email */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />

                {/* Encargado */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Encargado</label>
                <input type="text" value={encargado} onChange={e => setEncargado(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />

                {/* Forma de Pago */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Forma de Pago</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 print:appearance-none transition-colors">
                     <option value="">Seleccione...</option>
                    {parameters.paymentMethods?.map(method => <option key={method} value={method}>{method}</option>)}
                </select>

                {/* Entrega */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Entrega</label>
                <input type="text" value={entrega} onChange={e => setEntrega(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />

                {/* Validez */}
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 md:text-right md:col-span-1 print:col-span-1 print:text-xs transition-colors">Válida por</label>
                <input type="text" value={validez} onChange={e => setValidez(e.target.value)} className="md:col-span-5 print:col-span-5 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />
            </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg print:shadow-none print:border transition-colors">
            <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200 print:text-base transition-colors">Ítems de la Cotización</h2>
             <div className="grid grid-cols-12 gap-3 items-center mb-2 text-xs font-bold text-gray-500 dark:text-gray-400 print:gap-1 transition-colors">
                <div className="col-span-1 print:col-span-1">Cant.</div>
                <div className="col-span-1 print:col-span-1">Unid.</div>
                <div className="col-span-5 print:col-span-6">Descripción</div>
                <div className="col-span-1 print:hidden">Costo</div>
                <div className="col-span-2 text-right print:col-span-2">Precio Unit.</div>
                <div className="col-span-2 text-right print:col-span-2">Total</div>
            </div>
            {items.map((item, index) => {
                const totalItem = (item.price || 0) * (item.quantity || 0);
                return (
                    <div key={item.tempId || index} className="grid grid-cols-12 gap-3 items-start mb-2 print:gap-1 relative transition-colors">
                        <input type="number" placeholder="Cant." value={item.quantity} onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} className="col-span-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />
                        <select value={item.unit} onChange={e => handleItemChange(index, 'unit', e.target.value)} className="col-span-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 print:appearance-none transition-colors">
                            {parameters.unidades?.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        {/* --- Campo Descripción con Búsqueda --- */}
                        <div className="col-span-5 print:col-span-6 relative">
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Descripción (buscar producto...)"
                                    value={item.description}
                                    onChange={e => handleDescriptionSearchChange(index, e.target.value)}
                                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors"
                                />
                                {/* Iconos de Documentos */}
                                {(item.imageUrl || item.pdfUrl) && (
                                    <div className="flex gap-1 shrink-0 print:hidden">
                                                {item.imageUrl && (
                                                    <div className="group relative">
                                                        <PhotoIcon className="w-5 h-5 text-blue-500 cursor-help"/>
                                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-32">
                                                            <img src={getDirectUrl(item.imageUrl)} alt="item" className="rounded shadow-lg border dark:border-gray-600 bg-white dark:bg-gray-800"/>
                                                        </div>
                                                    </div>
                                                )}
                                                {item.pdfUrl && (
                                                    <a href={item.pdfUrl} target="_blank" rel="noopener noreferrer" title="Ver Ficha Técnica">
                                                        <DocumentTextIcon className="w-5 h-5 text-red-500"/>
                                                    </a>
                                                )}
                                    </div>
                                )}
                            </div>

                            {item.searchResults && item.searchResults.length > 0 && (
                                <ul className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg text-gray-900 dark:text-gray-100 transition-colors">
                                    {item.searchResults.map(p => (
                                        <li
                                            key={p.id}
                                            onClick={() => handleProductSelect(index, p)}
                                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm flex justify-between items-center transition-colors"
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-medium">{p.name} {p.type === 'kit' ? '(Kit)' : ''}</span>
                                                <span className={`text-xs flex items-center gap-1 ${p.stock <= 5 ? 'text-red-500 dark:text-red-400 font-bold' : 'text-green-600 dark:text-green-400'}`}>
                                                    <ArchiveBoxIcon className="w-3 h-3"/> Stock: {p.stock || 0}
                                                </span>
                                            </div>
                                            <span className="text-gray-400 dark:text-gray-500 text-xs flex gap-1 items-center">
                                                {p.imageUrl && <PhotoIcon className="w-3 h-3"/>}
                                                {p.pdfUrl && <DocumentTextIcon className="w-3 h-3"/>}
                                                <span className="ml-2 font-semibold text-gray-600 dark:text-gray-300">{formatCurrency(p.cost)}</span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="col-span-1 print:hidden">
                            <input type="number" placeholder="Costo" value={item.cost || ''} onChange={e => handleItemChange(index, 'cost', parseFloat(e.target.value) || 0)} className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-none print:p-0 transition-colors" />
                        </div>
                        <div className="col-span-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md text-right print:bg-transparent print:p-0 transition-colors">
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 print:text-xs transition-colors">{formatCurrency(item.price || 0)}</span>
                        </div>
                         <div className="col-span-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md text-right print:bg-transparent print:p-0 transition-colors">
                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100 print:text-xs transition-colors">{formatCurrency(totalItem)}</span>
                        </div>
                        <button type="button" onClick={() => removeItemRow(index)} className="col-span-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 h-10 w-10 rounded-full flex items-center justify-center hover:bg-red-200 dark:hover:bg-red-900/50 print:hidden transition-colors"><TrashIcon className="w-4 h-4"/></button>
                    </div>
                )
            })}
            <button type="button" onClick={addItemRow} className="mt-4 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/50 print:hidden transition-colors">+ Añadir Ítem</button>
        </div>

        {/* Detalles y Observaciones */}
         <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg print:shadow-none print:border transition-colors">
            <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200 print:text-base transition-colors">Detalles y Observaciones</h2>
            <textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                rows="4"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm print:border-gray-200 print:text-xs transition-colors"
                style={{ resize: 'vertical' }}
            ></textarea>
        </div>

        {/* Totales */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg print:shadow-none print:border transition-colors">
            <div className="flex justify-end text-gray-800 dark:text-gray-200 transition-colors">
                <div className="w-full md:w-1/3 space-y-2 print:w-1/2">
                    <div className="flex justify-between text-sm print:text-xs">
                        <span className="font-semibold">Neto:</span>
                        <span>{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm print:text-xs">
                        <span className="font-semibold">IVA ({parameters.iva}%):</span>
                        <span>{formatCurrency(ivaAmount)}</span>
                    </div>
                    <div className="flex justify-between text-xl print:text-base font-bold border-t dark:border-gray-700 pt-2 transition-colors">
                        <span>Total:</span>
                        <span>{formatCurrency(total)}</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Imágenes Referenciales */}
         <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg print:shadow-none print:border print:break-before-auto transition-colors">
            <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200 print:text-base transition-colors">Imagenes Referenciales (Cotización)</h2>
            <button type="button" onClick={() => fileInputRef.current.click()} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-200 dark:hover:bg-green-900/50 print:hidden transition-colors">
                📷 Adjuntar Fotos
            </button>
            <input type="file" ref={fileInputRef} onChange={handlePhotoChange} multiple accept="image/*" className="hidden" />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-2">
                {photoPreviews.map((photo, index) => (
                    <div key={index} className="relative border dark:border-gray-600 p-1 rounded-lg transition-colors">
                        <img src={photo.src} alt={`preview ${index}`} className="w-full h-32 object-cover rounded-lg print:h-auto print:object-contain"/>
                        <button type="button" onClick={() => removePhoto(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs print:hidden hover:bg-red-600 transition-colors">&times;</button>
                        {uploadProgress[index] > 0 && uploadProgress[index] < 100 && 
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 rounded-b-lg transition-colors"><div className="bg-blue-500 h-1" style={{width: `${uploadProgress[index]}%`}}></div></div>
                        }
                    </div>
                ))}
            </div>
        </div>
    </div>
);


const QuoteFormPage = ({ quoteId, setPage, setNotification }) => {
    const { user } = useAuth(); 
    const isEditMode = !!quoteId;
    const [folio, setFolio] = useState(quoteId || 'Por Asignar');
    
    // Estados de datos
    const [rut, setRut] = useState('');
    const [clientName, setClientName] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [encargado, setEncargado] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [entrega, setEntrega] = useState('');
    const [validez, setValidez] = useState('');
    const [details, setDetails] = useState('');
    const [items, setItems] = useState([{ tempId: Date.now(), quantity: 1, unit: 'unid', description: '', cost: 0, price: 0, searchResults: [] }]);
    const [subtotal, setSubtotal] = useState(0);
    const [ivaAmount, setIvaAmount] = useState(0);
    const [total, setTotal] = useState(0);
    const [parameters, setParameters] = useState({ iva: 19, unidades: ['unid'], paymentMethods: ['Contado'], headerInfo: '', footerInfo: '', gastosFijos: 0, utilidad: 0, bankName: '', bankRut: '', bankAccount: '', bankAccountType: '', bankEmail: '' });
    
    // Lógica de fotos
    const [photoPreviews, setPhotoPreviews] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});
    
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const fileInputRef = useRef(null);

    const [includeBankDetails, setIncludeBankDetails] = useState(true);

    // Estados para búsqueda de clientes
    const [allClients, setAllClients] = useState([]);
    const [clientNameSearch, setClientNameSearch] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const clientSearchTimeout = useRef(null);
    const clientNameInputRef = useRef(null);
    const clientSuggestionsRef = useRef(null);

    const printClientNameInputRef = useRef(null);
    const printClientSuggestionsRef = useRef(null);
    
    const [allProducts, setAllProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);

    // --- CARGA DE DATOS INICIALES ---
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const [clients, data, params] = await Promise.all([
                    clientService.getAll(),
                    productService.getInitialData(),
                    quoteService.getSystemParams()
                ]);

                setAllClients(clients);
                setAllProducts(data.products);
                
                // Merge params default
                const defaultParams = { iva: 19, unidades: ['unid'], paymentMethods: ['Contado'], headerInfo: '', footerInfo: '', gastosFijos: 0, utilidad: 0, bankName: '', bankRut: '', bankAccount: '', bankAccountType: '', bankEmail: '' };
                const finalParams = { ...defaultParams, ...params };
                setParameters(finalParams);
                
                if (!isEditMode) {
                    if (finalParams.paymentMethods?.length > 0) setPaymentMethod(finalParams.paymentMethods[0]);
                    if (finalParams.unidades?.length > 0) setItems([{ tempId: Date.now(), quantity: 1, unit: finalParams.unidades[0], description: '', cost: 0, price: 0, searchResults: [] }]);
                }

            } catch (error) {
                setNotification({ message: 'Error cargando datos.', type: 'error' });
            } finally {
                setLoading(false);
                setLoadingProducts(false);
            }
        };

        fetchInitialData();
    }, [isEditMode, setNotification]);

    // --- CARGA DE COTIZACIÓN (EDICIÓN) ---
    useEffect(() => {
        if (!isEditMode) return;

        const fetchQuote = async () => {
            setLoading(true);
            try {
                const data = await quoteService.getById(quoteId);
                if (data) {
                    setRut(formatRut(data.clientId || '')); 
                    setClientName(data.clientName || ''); 
                    setClientNameSearch(data.clientName || ''); 
                    setAddress(data.address || ''); 
                    setPhone(formatPhone(data.phone || '')); 
                    setEmail(data.email || ''); 
                    setEncargado(data.encargado || ''); 
                    setPaymentMethod(data.paymentMethod || ''); 
                    setEntrega(data.entrega || ''); 
                    setValidez(data.validez || ''); 
                    setDetails(data.details || '');
                    setItems(data.items.map(item => ({...item, cost: item.cost || 0, price: item.price || 0, tempId: Date.now() + Math.random(), searchResults: [], imageUrl: item.imageUrl || '', pdfUrl: item.pdfUrl || '' })));
                    if (data.fotos) { setPhotoPreviews(data.fotos.map(f => ({ src: f.url, name: f.name, file: null }))); }
                } else {
                    setNotification({ message: 'Cotización no encontrada.', type: 'error' });
                    setPage('quotes');
                }
            } catch (error) {
                setNotification({ message: 'Error al cargar cotización.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchQuote();
    }, [quoteId, isEditMode, setPage, setNotification]);


    // --- CÁLCULOS DE PRECIOS AUTOMÁTICOS ---
    useEffect(() => {
        const overheadPercentage = parseFloat(parameters.gastosFijos) / 100 || 0;
        const profitPercentage = parseFloat(parameters.utilidad) / 100 || 0;

        const itemsWithUpdatedPrice = items.map(item => {
             const cost = parseFloat(item.cost) || 0;
             // Si el precio es 0 o parece no haber sido seteado manualmente, sugerimos el calculado
             // PERO si ya tiene precio, hay que tener cuidado de no sobrescribir ediciones manuales.
             // En esta versión simple, recalculamos siempre basado en costo + margen configurado globalmente
             // Si quieres permitir precio manual libre que no se recalcule, necesitarías un flag "manualPrice"
             const unitPrice = cost * (1 + overheadPercentage + profitPercentage);
             return { ...item, price: unitPrice };
        });

        // Evitar loop infinito comparando valores
        const hasChanges = items.some((item, i) => Math.abs((item.price || 0) - (itemsWithUpdatedPrice[i].price || 0)) > 0.001);
        
        if (hasChanges) {
             setItems(itemsWithUpdatedPrice);
        }

        const newSubtotal = itemsWithUpdatedPrice.reduce((acc, item) => {
            const unitPrice = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            return acc + (unitPrice * quantity);
        }, 0);

        setSubtotal(newSubtotal);
        const newIvaAmount = newSubtotal * (parameters.iva / 100);
        setIvaAmount(newIvaAmount);
        setTotal(newSubtotal + newIvaAmount);

    }, [items, parameters.gastosFijos, parameters.utilidad, parameters.iva]);

    // --- HELPERS UI ---
    const handleRutChange = (e) => { setRut(formatRut(e.target.value)); };

    const handleClientNameSearchChange = (e) => {
        const name = e.target.value;
        setClientNameSearch(name);
        setClientName(name); 

        if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);

        if (name.length < 3) {
            setClientSuggestions([]);
            setShowClientSuggestions(false);
            return;
        }

        clientSearchTimeout.current = setTimeout(() => {
            const searchTerm = name.toLowerCase();
            const suggestions = allClients.filter(client => 
                client.nombre.toLowerCase().includes(searchTerm)
            ).slice(0, 10);
            
            setClientSuggestions(suggestions);
            setShowClientSuggestions(suggestions.length > 0);
        }, 100);
    };

    const handleSelectClient = (client) => {
        setClientName(client.nombre || '');
        setClientNameSearch(client.nombre || '');
        setRut(formatRut(client.rut || client.id || ''));
        setAddress(client.direccion || '');
        setPhone(formatPhone(client.telefono || ''));
        setEmail(client.email || '');
        setEncargado(client.encargado || '');
        setShowClientSuggestions(false);
    };

    const handleRutBlur = () => {
        const formattedRut = formatRut(rut);
        setRut(formattedRut);
        if (formattedRut.length < 8) return;
        const cleanRut = formattedRut.replace(/[.-]/g, '');

        const localMatch = allClients.find(c => c.id === cleanRut || c.rut === cleanRut);
        if (localMatch) {
            handleSelectClient(localMatch);
            setNotification({ message: `Cliente "${localMatch.nombre}" cargado.`, type: 'success' });
        } 
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                clientNameInputRef.current && !clientNameInputRef.current.contains(event.target) &&
                clientSuggestionsRef.current && !clientSuggestionsRef.current.contains(event.target)
            ) {
                setShowClientSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- MANEJO DE ITEMS ---
    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'cost') {
            // Recalculo inmediato local para UX rápida
            const cost = parseFloat(value) || 0;
            const overheadPercentage = parseFloat(parameters.gastosFijos) / 100 || 0;
            const profitPercentage = parseFloat(parameters.utilidad) / 100 || 0;
            newItems[index].price = cost * (1 + overheadPercentage + profitPercentage);
        }
        if (field !== 'description') {
            newItems[index].searchResults = [];
        }
        setItems(newItems);
    };

    const handleDescriptionSearchChange = (index, value) => {
        const newItems = [...items];
        newItems[index].description = value;

        if (value.length > 2 && !loadingProducts && allProducts.length > 0) {
            newItems[index].searchResults = allProducts.filter(p =>
                p.name.toLowerCase().includes(value.toLowerCase())
            );
        } else {
            newItems[index].searchResults = [];
        }
        setItems(newItems);
    };

    const handleProductSelect = (index, selectedProduct) => {
        const newItems = [...items];
        const overheadPercentage = parseFloat(parameters.gastosFijos) / 100 || 0;
        const profitPercentage = parseFloat(parameters.utilidad) / 100 || 0;

        if (selectedProduct.type === 'kit') {
            setNotification({ message: `Desglosando kit "${selectedProduct.name}"...`, type: 'info' });
            if (!Array.isArray(selectedProduct.components)) {
                 setNotification({ message: `Error: El kit "${selectedProduct.name}" no tiene componentes.`, type: 'error' });
                 return;
            }

            const kitComponentRows = selectedProduct.components.map(comp => {
                const componentData = allProducts.find(p => p.id === comp.productId && p.type === 'simple');
                if (!componentData) {
                    return {
                        tempId: Date.now() + Math.random(),
                        quantity: comp.quantity || 1,
                        unit: '?',
                        description: comp.name || `Componente ID ${comp.productId}`,
                        cost: 0, price: 0, searchResults: [],
                        imageUrl: '', pdfUrl: ''
                    };
                }
                const componentPrice = (componentData.cost || 0) * (1 + overheadPercentage + profitPercentage);
                return {
                    tempId: Date.now() + Math.random(),
                    quantity: comp.quantity || 1,
                    unit: componentData.unit || 'unid',
                    description: componentData.name,
                    cost: componentData.cost || 0,
                    price: componentPrice,
                    searchResults: [],
                    imageUrl: componentData.imageUrl || '',
                    pdfUrl: componentData.pdfUrl || ''
                };
            });
            newItems.splice(index, 1, ...kitComponentRows); 

        } else {
            const calculatedPrice = (selectedProduct.cost || 0) * (1 + overheadPercentage + profitPercentage);
            newItems[index] = {
                ...newItems[index],
                description: selectedProduct.name,
                unit: selectedProduct.unit || 'unid',
                cost: selectedProduct.cost || 0,
                price: calculatedPrice,
                searchResults: [],
                imageUrl: selectedProduct.imageUrl || '',
                pdfUrl: selectedProduct.pdfUrl || ''
            };
        }
        setItems(newItems);
    };

    const addItemRow = () => {
        const defaultUnit = parameters.unidades?.[0] || 'unid';
        setItems([...items, { tempId: Date.now() + Math.random(), quantity: 1, unit: defaultUnit, description: '', cost: 0, price: 0, searchResults: [] }]);
    };

    const removeItemRow = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    // --- FOTOS ---
    const compressImage = async (file) => {
        const options = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true };
        try { return await imageCompression(file, options); } catch (error) { console.error(error); return file; }
    };
    const handlePhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        setNotification({ message: 'Comprimiendo imágenes...', type: 'info' });
        try {
            const compressedFiles = await Promise.all(files.map(compressImage));
            const newPreviews = compressedFiles.map(file => ({
                src: URL.createObjectURL(file),
                name: file.name,
                file: file 
            }));
            setPhotoPreviews(prev => [...prev, ...newPreviews]);
            setNotification({ message: 'Imágenes listas para subir.', type: 'success' });
        } catch (error) { setNotification({ message: 'Error al procesar imágenes.', type: 'error' }); }
    };
    const removePhoto = (indexToRemove) => {
        setPhotoPreviews(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    // --- SUBMIT ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (items.length === 0) { setNotification({ message: 'Debe añadir al menos un ítem.', type: 'error' }); return; }

        setLoading(true); setLoadingMessage('Guardando...');
        const cleanRut = rut.replace(/[.-]/g, '');
        
        try {
            // Sanitizar items
            const validItems = items
                .filter(item => item.description.trim() !== '' || item.cost > 0 || item.quantity > 0)
                .map(({ searchResults, tempId, ...rest }) => ({
                    ...rest,
                    cost: Number(rest.cost) || 0,
                    price: Number(rest.price) || 0,
                    quantity: Number(rest.quantity) || 0
                })); 

            // Armar objeto
            const quoteData = {
                clientId: cleanRut, clientName, address, phone: phone.replace(/\s/g, ''), email, encargado, paymentMethod, entrega, validez, details,
                items: validItems, subtotal: Number(subtotal) || 0, ivaAmount: Number(ivaAmount) || 0, total: Number(total) || 0
            };

            // Llamar al servicio
            const finalFolio = await quoteService.saveQuote({
                quoteId: isEditMode ? quoteId : null,
                quoteData,
                photos: photoPreviews, // Pasamos los previews, el servicio separa nuevos de existentes
                isEditMode
            });

            setNotification({ message: `Cotización ${finalFolio} guardada.`, type: 'success' });
            setTimeout(() => setPage('quotes'), 1500);

        } catch (error) { 
            setNotification({ message: `Error: ${error.message}`, type: 'error' }); 
        }
        finally { setLoading(false); setLoadingMessage(''); }
    };

    if (loading && !isEditMode && (loadingProducts || allClients.length === 0)) return <div className="p-8 text-center">{loadingMessage || 'Cargando formulario...'}</div>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors print:bg-white text-gray-900 dark:text-white">
            <div className="print:hidden">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">{isEditMode ? `Modificar Cotización N°: ${quoteId}` : `Nueva Cotización: ${folio}`}</h1>
                    <button onClick={() => setPage(isEditMode ? 'viewQuote' : 'quotes', isEditMode ? quoteId : null)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold transition-colors">&larr; Volver</button>
                </div>
                {(loading && isEditMode) || loadingProducts ? <div className="p-8 text-center text-gray-600 dark:text-gray-400">{loadingMessage || 'Cargando...'}</div> : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <FormContent
                            items={items} handleItemChange={handleItemChange} addItemRow={addItemRow} removeItemRow={removeItemRow} parameters={parameters}
                            rut={rut} handleRutChange={handleRutChange} handleRutBlur={handleRutBlur} clientName={clientName} clientNameSearch={clientNameSearch}
                            handleClientNameSearchChange={handleClientNameSearchChange} clientNameInputRef={clientNameInputRef} clientSuggestions={clientSuggestions}
                            showClientSuggestions={showClientSuggestions} clientSuggestionsRef={clientSuggestionsRef} handleSelectClient={handleSelectClient}
                            setClientName={setClientName} address={address} setAddress={setAddress} phone={phone} setPhone={setPhone} email={email} setEmail={setEmail}
                            encargado={encargado} setEncargado={setEncargado} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
                            entrega={entrega} setEntrega={setEntrega} validez={validez} setValidez={setValidez} details={details} setDetails={setDetails}
                            photoPreviews={photoPreviews} removePhoto={removePhoto} uploadProgress={uploadProgress} fileInputRef={fileInputRef} handlePhotoChange={handlePhotoChange}
                            subtotal={subtotal} ivaAmount={ivaAmount} total={total} allProducts={allProducts} handleDescriptionSearchChange={handleDescriptionSearchChange}
                            handleProductSelect={handleProductSelect}
                        />
                        <div className="flex justify-end space-x-4 items-center mt-6">
                            <label className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 p-2 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 mr-4 transition-colors">
                                <input type="checkbox" checked={includeBankDetails} onChange={(e) => setIncludeBankDetails(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Incluir Datos Bancarios al Final</span>
                            </label>
                            
                            <button type="submit" disabled={loading || loadingProducts} className="bg-blue-600 dark:bg-blue-700 text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition disabled:bg-blue-300 dark:disabled:bg-blue-800/50 font-semibold">
                                {loading ? loadingMessage : (isEditMode ? 'Actualizar Cotización' : 'Guardar Cotización')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
            {/* VISTA OCULTA DE IMPRESIÓN (Si se requiere pre-renderizado) */}
            <div className="hidden print:block relative">
                 {/* Lógica de impresión manejada en ViewQuotePage, pero si se requiere imprimir directo desde el form, se usa este bloque */}
            </div>
        </div>
    );
};

export default QuoteFormPage;